import React, { useContext, useEffect, useState } from 'react';
import './DocumentsTable.scss';
import { Card, Layout, Paragraph, Table } from '@vwfs-bronson/bronson-react';
import { IDocumentContext } from '../../../context/DocCenterContext.interface';
import { DocCenterContext } from '../../../context/DocCenterContext';
import TableRow from './TableRow';
import { useAuthContext } from '../../../context/AuthContext';
import i18n from '../../../localization/i18n';
import * as rk from '../../../localization/resourceKeys';

const DocumentsTable: React.FC = () => {
  const { filteredStatements, filteredLoanAgreements, filteredAutopayLetters, blankData } = useContext(
    DocCenterContext
  ) as IDocumentContext;
  const { state: authState } = useAuthContext();
  const [endLoad, setEndLoad] = useState(12);
  const [hideBtn, setHideBtn] = useState('');
  const [documentsShown, setDocumentsShown] = useState(0);
  const documentsQuantity = filteredStatements.length + filteredLoanAgreements.length + filteredAutopayLetters.length;
  const load = [
    filteredStatements?.slice(0, endLoad),
    filteredLoanAgreements?.slice(0, endLoad),
    filteredAutopayLetters?.slice(0, endLoad),
  ];
  const yearIndex: Array<number> = [];
  const errorIcon = authState.brand === 'VW' ? 'semantic-error' : 'caution';

  const handleLoadMore = () => {
    if (documentsQuantity > documentsShown) {
      setHideBtn('');
      setEndLoad(endLoad + 12);
      // Error management of no more documents to load
    }
  };

  const handleYearTables = () => {
    const yearSet = new Set();
    load.forEach((document) => {
      document.forEach((doc) => {
        let documentDate;
        if (doc) {
          if (doc.InvoiceDate) {
            documentDate = new Date(doc.InvoiceDate);
          } else if (doc.CreateDate) {
            documentDate = new Date(doc.CreateDate);
          } else return;
          const year = documentDate.getFullYear();
          yearSet.add(year);
        }
      });
    });
    yearIndex.push(...yearSet);
  };

  let counter = 0;

  const documentRows = (tableYear: number) => {
    const flatLoad = load.flat();
    return flatLoad.map((doc, index) => {
      let docYear;
      if (doc) {
        if (doc.InvoiceDate) {
          docYear = new Date(doc.InvoiceDate).getFullYear();
        } else if (doc.CreateDate) {
          docYear = new Date(doc.CreateDate).getFullYear();
        } else return null;
        if (docYear === tableYear && JSON.stringify(doc) !== '{}' && counter < endLoad) {
          counter += 1;
          // eslint-disable-next-line react/no-array-index-key
          return <TableRow file={doc} key={`table-row-${index}`} />;
        }
      }
      return null;
    });
  };

  const documentYears = () => {
    handleYearTables();
    return yearIndex.map((year) => {
      /* eslint-disable react/no-array-index-key */
      return (
        <>
          <div className='doc-table__year u-mb-xsmall'>
            <h6>{year}</h6>
          </div>
          <Table noScroll>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{i18n.t(rk.DOCUMENT_CENTER_TABLE_HEADER_DATE).toString()}</Table.Th>
                <Table.Th>{i18n.t(rk.DOCUMENT_CENTER_TABLE_HEADER_DOWNLOAD).toString()}</Table.Th>
                <Table.Th>{i18n.t(rk.DOCUMENT_CENTER_TABLE_HEADER_NAME).toString()}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{documentRows(year)}</Table.Tbody>
          </Table>
        </>
      );
    });
  };

  useEffect(() => {
    handleYearTables();
  }, [filteredStatements]);

  useEffect(() => {
    const documents = document.getElementsByClassName('doc-table-col__body');
    setDocumentsShown(documents.length);
  }, [filteredStatements, filteredLoanAgreements, filteredAutopayLetters, endLoad]);

  useEffect(() => {
    if (documentsQuantity === documentsShown) {
      setHideBtn('u-hide');
    } else {
      setHideBtn('');
    }
  }, [documentsShown]);

  return (
    <>
      {blankData === true ? (
        <Layout center flush>
          <Layout.Item xs='1/1' s='1/1' m='1/2' l='1/2' xl='1/3' default='1/3'>
            <article className='u-mt-none card-container'>
              <header className='c-card__header u-text-center'>
                <div className='c-card__icon--custom'>
                  <i className={`c-icon  c-icon--[${errorIcon}] no-docs__icon`} aria-hidden='true' role='img' />
                </div>
                <h4>{i18n.t(rk.DOCUMENT_CENTER_NO_DOCS_TITLE).toString()}</h4>
                <p>{i18n.t(rk.DOCUMENT_CENTER_NO_DOCS_NOTE).toString()}</p>
              </header>
            </article>
          </Layout.Item>
        </Layout>
      ) : (
        <div className='doc-tables-wrapper'>{documentYears()}</div>
      )}
      {(filteredStatements.length > 0 || filteredLoanAgreements.length > 0 || filteredAutopayLetters.length > 0) && (
        <div>
          {documentsQuantity > endLoad && (
            <div className=' o-layout__item load-more'>
              <div className=' load-more__container'>
                <button
                  type='button'
                  onClick={() => handleLoadMore()}
                  className={`c-btn  c-btn--secondary load-more__btn ${hideBtn}`}
                >
                  <span className='c-btn__text'>{i18n.t(rk.DOCUMENT_CENTER_BUTTON_LOAD_MORE).toString()}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DocumentsTable;
